"use client";

/**
 * An event's main banner, on its own: the event's own, or else its programme's.
 * Named for the view transition, so it morphs from the card it was opened from
 * in a list (event-banner-<id>). An event with none gets a soft brand ground
 * rather than a broken image.
 */
import { ViewTransition } from "react";
import { Mountain } from "lucide-react";
import { cdnUrl } from "@/lib/cdn";
import { eventBanner } from "@/lib/jsonLd";
import type { EventDetails } from "@/lib/types/event";

// how wide admin-api makes a banner's smaller copy (BANNER_THUMBNAIL_WIDTH in its lib/bannerImages.js)
const THUMBNAIL_WIDTH = 960;
// the page's width on a phone; on a desktop the banner is never wider than this
const BANNER_SIZES = "(min-width: 40rem) 640px, 100vw";

export const bannerTransitionName = (id: number | string) => `event-banner-${id}`;

export function EventBanner({
  event,
  className = "",
  imageClassName = "",
  priority = true,
  transition = true,
}: {
  event: Pick<EventDetails, "id" | "title" | "event_banners" | "banners">;
  className?: string;
  imageClassName?: string;
  priority?: boolean;
  /** false for a second copy of the banner on the same page: a name may be mounted once */
  transition?: boolean;
}) {
  const banner = eventBanner(event);
  return (
    <ViewTransition name={transition ? bannerTransitionName(event.id) : undefined}>
      <div className={`relative overflow-hidden bg-brand-mint ${className}`}>
        {banner ? (
          // the smaller copy on a phone, the banner itself where it is drawn large;
          // the browser picks, and a banner with no smaller copy has the one file
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cdnUrl(banner.path)}
            srcSet={banner.thumbnail ? `${cdnUrl(banner.thumbnail)} ${THUMBNAIL_WIDTH}w, ${cdnUrl(banner.path)} ${banner.width || 1920}w` : undefined}
            sizes={banner.thumbnail ? BANNER_SIZES : undefined}
            alt={event.title || ""}
            className={`block h-full w-full object-cover ${imageClassName}`}
            fetchPriority={priority ? "high" : undefined}
            decoding="async"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-brand/50">
            <Mountain size={56} strokeWidth={1.5} />
          </div>
        )}
      </div>
    </ViewTransition>
  );
}
